import {Component, OnInit, Input, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { distinctUntilChanged, tap } from 'rxjs/operators';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { BookingOptions } from '@shared/restaurant/models';
import {
  priceMinValueValidator,
  priceValidator,
  ValidationService,
} from '@tablesready/common-ui';
import { Schedule } from '@shared/schedule/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { Subscription } from 'rxjs';

@Component({
  selector: 'tablesready-add-edit-service',
  templateUrl: './add-edit-service.component.html',
  styleUrls: ['./add-edit-service.component.scss']
})
export class AddEditServiceComponent implements OnInit, OnDestroy {
  @Input() service;
  @Input() count;
  @Input() availableSchedules: Schedule[] = [];
  @Input() categories;
  @Input() bookingOptions: BookingOptions;
  @Input() publicUrl: string;
  @Input() isUserOnFreePlan: boolean;
  @Input() stripeEnabled = false;
  icon = AppIcon;
  title: string;
  pattern = "^([0-9]|[0-5][0-9])$";

  MINUTES_IN_HOUR = 60;
  SECONDS_IN_MINUTE = 60;
  MILLISECONDS_IN_SECONDS = 1000;

  serviceForm: UntypedFormGroup;
  origin: string;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly fb: UntypedFormBuilder,
    private readonly validationService: ValidationService,
  ) { }

  get priceControl(): AbstractControl {
    return this.serviceForm.get('price');
  }

  ngOnInit() {
    this.origin = window.location.origin;
    this.title = isEmpty(this.service) ? 'Add' : 'Edit';
    if (isEmpty(this.service)) {
      this.service = {
        availability: {
          waitlist: true,
          reservations: true,
        },
        description: '',
        category: '',
        duration: {
          hours: 0,
          minutes: 0,
          miliseconds: 0,
        },
        enabled: true,
        name: '',
        order: 0,
        price: '',
        schedule: this.availableSchedules.find(item => item.default)._id,
      };
    }

    this.serviceForm = this.fb.group({
      name: [this.service.name, [Validators.required]],
      enabled: [this.service.enabled],
      description: [this.service.description || '', this.stripeEnabled ? [Validators.required] : []],
      price: [this.service.price],
      availability: this.fb.group({
        waitlist: [this.service.availability.waitlist],
        reservations: [this.service.availability.reservations],
      }),
      schedule: [this.service.schedule, [Validators.required]],
      category: [this.service.category],
      duration: this.fb.group({
        hours: [this.service.duration.hours, [Validators.required, Validators.pattern(this.pattern)]],
        minutes: [this.service.duration.minutes, [Validators.required, Validators.pattern(this.pattern)]],
        miliseconds: [this.service.duration.miliseconds, [Validators.min(1)]]
      }),
      url: [
        this.service.url || '',
        [Validators.maxLength(255), Validators.pattern('^[a-z0-9\-]+$')],
        [
          (control: AbstractControl) => this.validationService.serviceUrlUsedValidator(control.value, this.service?._id),
        ],
      ],
    });

    if (this.stripeEnabled) {
      this.serviceForm.get('price').setValidators([priceValidator(), priceMinValueValidator()]);
    } else {
      this.serviceForm.get('price').setValidators([priceValidator()]);
    }

    const formValueSubscription = this.serviceForm.valueChanges
      .pipe(
        distinctUntilChanged(isEqual),
        tap(values => {
          this.durationToMiliseconds(values);
        }),
      )
      .subscribe();
    this.subscriptions.add(formValueSubscription);
  }

  confirm(service): void {
    if(this.service._id) {
      service._id = this.service._id;
    }
    service.price = service.price === '' || !service.price ? '' : service.price.toString();
    service.category = service.category || '';
    this.modalController.dismiss(service);
  }

  durationToMiliseconds(data) {
    const hours = get(data, 'duration.hours', 0),
      minutes = get(data, 'duration.minutes', 0),
      milliseconds = (hours * this.MINUTES_IN_HOUR + minutes) * this.SECONDS_IN_MINUTE * this.MILLISECONDS_IN_SECONDS;

    this.serviceForm.get('duration.miliseconds').patchValue(milliseconds, {
      onlySelf: true,
      emitEvent: false,
    });

    this.serviceForm.get('duration').updateValueAndValidity();
  };

  cancel(): void {
    this.modalController.dismiss();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
